import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { DragHandleProps } from './SortableBlock';

interface Props {
  blockId: string;
  title: string;
  onUpdate: (blockId: string, title: string) => Promise<void>;
  onDelete: (blockId: string) => Promise<void>;
  dragHandleProps?: DragHandleProps;
}

export default function HeadingBlock({ blockId, title, onUpdate, onDelete, dragHandleProps }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleStartEdit = () => {
    setDraft(title);
    setEditing(true);
  };

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== title) {
      await onUpdate(blockId, trimmed);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(title);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (editing) {
    return (
      <Box sx={{ mb: 1, position: 'relative' }}>
        <TextField
          inputRef={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          variant="standard"
          fullWidth
          slotProps={{
            input: {
              sx: {
                fontSize: 20,
                fontWeight: 600,
                color: '#111827',
              },
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        '&:hover .block-delete-btn': { opacity: 1 },
        '&:hover .block-drag-handle': { opacity: 1 },
      }}
    >
      {dragHandleProps && (
        <Box
          ref={dragHandleProps.ref}
          {...dragHandleProps.attributes}
          {...dragHandleProps.listeners}
          className="block-drag-handle"
          sx={{
            display: 'flex',
            cursor: 'grab',
            color: 'text.disabled',
            flexShrink: 0,
            opacity: 0,
            transition: 'opacity 150ms ease-in-out',
            ml: -3,
          }}
          aria-label={`Reorder ${title}`}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      <Typography
        onClick={handleStartEdit}
        sx={{
          fontSize: 20,
          fontWeight: 600,
          color: '#111827',
          cursor: 'text',
          pr: 4,
          flex: 1,
        }}
      >
        {title}
      </Typography>
      <IconButton
        className="block-delete-btn"
        size="small"
        onClick={() => onDelete(blockId)}
        aria-label="Delete heading"
        sx={{
          position: 'absolute',
          top: -4,
          right: -4,
          opacity: 0,
          transition: 'opacity 150ms ease-in-out',
          color: 'text.disabled',
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
